import React, { useState, useMemo } from 'react'
import PropTypes from 'prop-types'

import { prop } from 'ramda'
import { classNames } from 'utils/utils'
import { insertIf } from 'utils/types'
import strings from 'utils/strings'
import { DEFAULT, RESET, SAVE } from 'constants/ui/editor'

import InfoOverlay from 'components/InfoOverlay'
import withStyle, {
  StyledContent,
  Footer,
  StyledDivider,
  StyledBreadcrumb,
} from './Editor.style.js'

const Editor = ({
  className,
  children,
  actions: { onReset, onSave, onRemove, custom },
  overlayContent,
  active,
  hasModifications,
  hasErrors,
  hasEditInProgress,
  breadcrumb,
  onBreadcrumbClick,
}) => {
  const [notes, setNotes] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [editorState, setEditorState] = useState(DEFAULT)
  const setDefaultEditorState = () => setEditorState(DEFAULT)
  const showCustom = prop('flag', custom)
  const showOverlay = useMemo(() => editorState !== DEFAULT, [editorState])

  const handleSave = () => {
    setSubmitting(true)

    onSave(notes, () => {
      setDefaultEditorState()
      setNotes('')
      setSubmitting(false)
    })
  }

  const handleReset = () => {
    onReset(() => setDefaultEditorState())
  }

  const handleCancelSave = () => {
    setDefaultEditorState()
    setNotes('')
  }

  const actionBarButtons = {
    [DEFAULT]: [
      ...insertIf(onSave, {
        text: strings.save,
        type: 'primary',
        disabled: hasErrors || !hasModifications,
        action: () => setEditorState(SAVE),
        dataTestId: 'save-btn',
        className: 'save-btn',
      }),
      ...insertIf(onReset, {
        text: strings.reset,
        disabled: !hasModifications && !hasEditInProgress,
        type: 'secondary',
        action: () => setEditorState(RESET),
        dataTestId: 'reset-btn',
        className: 'reset-btn',
      }),
      ...insertIf(onRemove, {
        text: strings.remove,
        type: 'tertiary',
        action: onRemove,
        dataTestId: 'remove-btn',
        className: 'remove-btn',
      }),
      ...insertIf(showCustom, {
        text: prop('customLabel', custom),
        type: 'custom',
        dataTestId: 'custom-btn',
        className: 'custom-btn',
      }),
    ],
    [RESET]: [
      {
        text: strings.reset,
        type: 'danger',
        action: handleReset,
        dataTestId: 'confirm-reset-btn',
        className: 'confirm-reset-btn',
      },
      {
        text: strings.cancel,
        type: 'secondary',
        action: setDefaultEditorState,
        dataTestId: 'cancel-reset-btn',
        className: 'cancel-reset-btn',
      },
    ],
    [SAVE]: [
      {
        text: strings.save,
        type: 'primary',
        action: handleSave,
        loading: submitting,
        disabled: notes.length === 0,
        dataTestId: 'confirm-changes-btn',
        className: 'confirm-changes-btn',
      },
      {
        text: strings.reset,
        type: 'secondary',
        action: handleReset,
        disabled: submitting,
        dataTestId: 'reset-changes-btn',
        className: 'reset-changes-btn',
      },
    ],
  }[editorState]

  if (!active) return children

  return (
    <div className={classNames(className, 'editor')}>
      {showOverlay && breadcrumb && (
        <StyledBreadcrumb>
          {typeof breadcrumb === 'function'
            ? breadcrumb(handleCancelSave)
            : breadcrumb}
        </StyledBreadcrumb>
      )}
      {showOverlay ? (
        <InfoOverlay
          notes={notes}
          setNotes={setNotes}
          infoState={editorState}
          content={overlayContent}
          submitting={submitting}
        />
      ) : (
        <StyledContent>{children}</StyledContent>
      )}
      <StyledDivider />
      <Footer
        className="editor-footer"
        buttons={actionBarButtons}
        buttonType={DEFAULT}
      />
    </div>
  )
}

Editor.defaultProps = {
  children: null,
  actions: {
    onSave: () => {},
    onReset: () => {},
    onRemove: null,
  },
  submitDisabled: false,
  active: true,
}

Editor.propTypes = {
  /** Classname */
  className: PropTypes.string,
  /** when false, no editor content will be shown */
  active: PropTypes.bool,
  children: PropTypes.node,
  /** functions for action bar */
  actions: PropTypes.shape({
    onReset: PropTypes.func.isRequired,
    onSave: PropTypes.func,
    onRemove: PropTypes.func,
    custom: PropTypes.shape({
      flag: PropTypes.bool,
      customLabel: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    }),
  }),
  /** disable submit, e.g. note is empty */
  submitDisabled: PropTypes.bool,
  /** JSX content for overlays */
  overlayContent: PropTypes.shape({
    reset: PropTypes.object,
    save: PropTypes.object,
  }),
  /** used to hide buttons when no modifications are made */
  hasModifications: PropTypes.bool,
  hasErrors: PropTypes.bool,
  hasEditInProgress: PropTypes.bool,
  /** Breadcrumb content to show when overlay is visible. Can be a node or function receiving closeOverlay callback */
  breadcrumb: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  /** Callback when breadcrumb is clicked */
  onBreadcrumbClick: PropTypes.func,
}

export default withStyle(Editor)
